import React from 'react';

/**
 * Mobile & Tablet Compatibility Utilities for Payment SDKs (Mercado Pago, InfinitePay, Stripe, PayPal)
 * Prevents "The string did not match the expected pattern" errors on mobile WebKit / Safari / iPadOS
 * and ensures safe, deferred SDK initialization after DOM readiness.
 */

/**
 * Detects whether the current device is a mobile phone, tablet (iPad, Android tablet), or touch device.
 */
export function isMobileOrTablet(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  
  const ua = navigator.userAgent || navigator.vendor || (window as any).opera || '';
  
  // Standard mobile / tablet User-Agent regex
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet|silk|kindle/i;
  
  // iPadOS 13+ detection (reports as MacIntel but has touch points)
  const isiPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  
  // Touch capability + screen width check
  const isTouchScreen = (navigator.maxTouchPoints > 0 || 'ontouchstart' in window) && window.innerWidth <= 1024;
  
  return mobileRegex.test(ua) || isiPadOS || isTouchScreen;
}

/**
 * Returns a Promise that resolves when the document is fully loaded and ready.
 * On mobile/tablets, waits for document.readyState === 'complete' or DOMContentLoaded + delay.
 */
export function ensurePageAndDOMReady(minDelayMs = 100): Promise<void> {
  return new Promise((resolve) => {
    if (typeof document === 'undefined') {
      resolve();
      return;
    }

    const onReady = () => {
      setTimeout(() => resolve(), isMobileOrTablet() ? Math.max(minDelayMs, 150) : minDelayMs);
    };

    if (document.readyState === 'complete') {
      onReady();
    } else {
      const handler = () => {
        window.removeEventListener('load', handler);
        document.removeEventListener('DOMContentLoaded', handler);
        onReady();
      };
      window.addEventListener('load', handler);
      document.addEventListener('DOMContentLoaded', handler);
    }
  });
}

/**
 * Checks if a specific DOM element exists and has non-zero computed layout dimensions.
 * Retries up to `maxRetries` times with interval if element is not ready yet on mobile.
 */
export function ensureContainerReady(
  containerIdOrRef: string | React.RefObject<HTMLElement>,
  maxRetries = 10,
  intervalMs = 100
): Promise<HTMLElement | null> {
  return new Promise((resolve) => {
    let attempts = 0;

    const check = () => {
      attempts++;
      let el: HTMLElement | null = null;

      if (typeof containerIdOrRef === 'string') {
        el = document.getElementById(containerIdOrRef);
      } else if (containerIdOrRef && containerIdOrRef.current) {
        el = containerIdOrRef.current;
      }

      if (el && (el.offsetWidth > 0 || el.offsetHeight > 0 || attempts >= maxRetries)) {
        resolve(el);
      } else if (attempts < maxRetries) {
        setTimeout(check, intervalMs);
      } else {
        resolve(el);
      }
    };

    check();
  });
}

/**
 * Sanitizes and validates payment URLs (for Mercado Pago, InfinitePay, Stripe, PayPal).
 * Guarantees that invalid patterns, relative URLs, or unescaped parameters never throw
 * "DOMException: The string did not match the expected pattern" in WebKit / Safari.
 */
export function sanitizePaymentUrl(rawUrl: string | null | undefined, fallbackUrl?: string): string {
  const defaultFallback = fallbackUrl || (typeof window !== 'undefined' ? window.location.origin : 'https://dojapao.co.jp');
  
  if (!rawUrl || typeof rawUrl !== 'string') return defaultFallback;
  
  const trimmed = rawUrl.trim();
  if (!trimmed) return defaultFallback;

  try {
    // Ensure absolute protocol
    let formatted = trimmed;
    if (!/^https?:\/\//i.test(formatted)) {
      formatted = `https://${formatted.replace(/^\/+/, '')}`;
    }

    // Try parsing with native URL
    const parsed = new URL(formatted);
    
    // Ensure hostname is valid
    if (!parsed.hostname || parsed.hostname.length < 3) {
      return defaultFallback;
    }

    return parsed.toString();
  } catch (err) {
    console.warn('⚠️ [sanitizePaymentUrl] String pattern invalid, returning fallback:', rawUrl, err);
    return defaultFallback;
  }
}

/**
 * Safely opens a URL in a new window or tab.
 * Avoids WebKit DOMException errors by avoiding invalid 3rd window.open parameter strings.
 */
export function safeWindowOpen(url: string, target = '_blank'): boolean {
  if (typeof window === 'undefined') return false;

  const validUrl = sanitizePaymentUrl(url);

  try {
    // Simple window.open without 3rd parameter string to avoid WebKit 'string pattern' exceptions
    const newWin = window.open(validUrl, target);
    if (newWin) {
      try {
        newWin.focus();
      } catch (e) {}
      return true;
    }
  } catch (err) {
    console.warn('⚠️ [safeWindowOpen] Direct window.open failed, setting location.href:', err);
  }

  // Fallback: assign window.location directly if popup was blocked or threw
  try {
    window.location.href = validUrl;
    return true;
  } catch (e) {
    console.error('❌ [safeWindowOpen] Failed to navigate:', e);
    return false;
  }
}

/**
 * Executes a payment SDK initialization function safely with page readiness, timeout, and retry protection.
 */
export async function safeInitPaymentSDK(
  initFn: () => Promise<void> | void,
  options: {
    timeoutMs?: number;
    retryCount?: number;
    componentName?: string;
  } = {}
): Promise<boolean> {
  const { timeoutMs = 8000, retryCount = 1, componentName = 'PaymentSDK' } = options;

  // 1. Ensure DOM is ready on mobile
  await ensurePageAndDOMReady();

  let attempt = 0;
  while (attempt <= retryCount) {
    attempt++;
    try {
      const resultPromise = Promise.resolve(initFn());
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error(`Timeout ao inicializar ${componentName} (${timeoutMs}ms)`)), timeoutMs)
      );

      await Promise.race([resultPromise, timeoutPromise]);
      return true;
    } catch (err: any) {
      console.warn(`⚠️ [${componentName}] Tentativa ${attempt} falhou:`, err?.message || err);
      if (attempt > retryCount) {
        return false;
      }
      // Wait before retry
      await new Promise((r) => setTimeout(r, 300));
    }
  }

  return false;
}
